import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useAuthStore } from '../../store/authStore';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { generatePuzzle, DifficultyLevel, Puzzle, CellState } from '../../utils/tasukueaEngine';
import { useAccessibility } from '../../contexts/AccessibilityContext';

// ─── Dificultades ─────────────────────────────────────────────

const DIFFICULTIES = [
  { id: 1, nombre: 'Neurona',  icono: '🧠', color: '#22c55e', level: DifficultyLevel.NEURONA },
  { id: 2, nombre: 'Sinapsis', icono: '⚡', color: '#3b82f6', level: DifficultyLevel.SINAPSIS },
  { id: 3, nombre: 'Cerebro',  icono: '🧩', color: '#a855f7', level: DifficultyLevel.CEREBRO },
  { id: 4, nombre: 'Genio',    icono: '💡', color: '#ef4444', level: DifficultyLevel.GENIO },
  { id: 5, nombre: 'Einstein', icono: '🎓', color: '#f97316', level: DifficultyLevel.EINSTEIN },
];

// Player cell state: 'unknown' | 'black' | 'white'
type PlayerState = 'unknown' | 'black' | 'white';

function cycleState(s: PlayerState): PlayerState {
  if (s === 'unknown') return 'black';
  if (s === 'black') return 'white';
  return 'unknown';
}

// ─── Componente principal ─────────────────────────────────────

export default function TasukueaGame() {
  const { dificultad: dificultadParam } = useParams<{ dificultad: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { highContrast } = useAccessibility();

  const difficulty = Math.max(1, Math.min(5, parseInt(dificultadParam || '1')));
  const currentDiff = DIFFICULTIES.find(d => d.id === difficulty) || DIFFICULTIES[0];
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;

  const { saveProgress: saveGameProgress } = useGameProgress('tasukuea', difficulty);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Estado
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [playerGrid, setPlayerGrid] = useState<PlayerState[][]>([]);
  const [loading, setLoading] = useState(true);
  const [solved, setSolved] = useState(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moves, setMoves] = useState(0);

  // Historial undo/redo
  const [history, setHistory] = useState<PlayerState[][][]>([]);
  const [histIdx, setHistIdx] = useState(-1);

  // Medición del tablero
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      setBoardW(boardAreaRef.current.clientWidth - 8);
      setBoardH(boardAreaRef.current.clientHeight - 8);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  // Timer
  useEffect(() => {
    if (solved) return;
    const t = setInterval(() => setElapsedTime(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(t);
  }, [solved, startTime]);

  // Generar puzzle
  const generateNewPuzzle = useCallback((forceNew = false) => {
    setLoading(true);
    setSolved(false);
    setMoves(0);
    // Run async to not block UI
    setTimeout(() => {
      try {
        const seed = forceNew ? undefined : seedParam;
        const p = generatePuzzle(currentDiff.level, seed);
        setPuzzle(p);
        const initGrid: PlayerState[][] = Array.from({ length: p.board.rows }, () =>
          Array(p.board.cols).fill('unknown')
        );
        setPlayerGrid(initGrid);
        setHistory([initGrid.map(row => [...row])]);
        setHistIdx(0);

        // Write seed to URL
        const newParams = new URLSearchParams(searchParams);
        newParams.set('seed', p.metadata.seed.toString());
        setSearchParams(newParams, { replace: true });
      } catch (e) {
        console.error(e);
        toast.error('Error generando puzzle');
      } finally {
        setLoading(false);
      }
    }, 50);
  }, [difficulty, seedParam, currentDiff.level]);

  useEffect(() => {
    generateNewPuzzle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty]);

  // Click en celda
  function handleCellClick(r: number, c: number) {
    if (solved || !puzzle) return;
    const cell = puzzle.board.grid[r][c];
    if (cell.clue !== null) return; // celda con número = fija

    const newGrid = playerGrid.map(row => [...row]);
    newGrid[r][c] = cycleState(newGrid[r][c]);
    setPlayerGrid(newGrid);
    setMoves(m => m + 1);
    commitHistory(newGrid);
    checkVictory(newGrid, puzzle);
  }

  function commitHistory(grid: PlayerState[][]) {
    setHistory(prev => {
      const truncated = prev.slice(0, histIdx + 1);
      const next = [...truncated, grid.map(row => [...row])];
      setHistIdx(next.length - 1);
      return next;
    });
  }

  function undo() {
    if (histIdx <= 0) return;
    const newIdx = histIdx - 1;
    setHistIdx(newIdx);
    setPlayerGrid(history[newIdx].map(row => [...row]));
  }

  function redo() {
    if (histIdx >= history.length - 1) return;
    const newIdx = histIdx + 1;
    setHistIdx(newIdx);
    setPlayerGrid(history[newIdx].map(row => [...row]));
  }

  function reset() {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const initGrid: PlayerState[][] = Array.from({ length: puzzle.board.rows }, () =>
      Array(puzzle.board.cols).fill('unknown')
    );
    setPlayerGrid(initGrid);
    setHistory([initGrid.map(row => [...row])]);
    setHistIdx(0);
    setMoves(0);
    setSolved(false);
  }

  function giveHint() {
    if (!puzzle || solved) return;
    // 1. Corregir un error (celda marcada como negra que debe ser blanca)
    for (let r = 0; r < puzzle.solution.rows; r++) {
      for (let c = 0; c < puzzle.solution.cols; c++) {
        if (puzzle.board.grid[r][c].clue !== null) continue;
        const real = puzzle.solution.grid[r][c].state;
        const ps = playerGrid[r][c];
        if (ps === 'black' && real === CellState.WHITE) {
          const newGrid = playerGrid.map(row => [...row]);
          newGrid[r][c] = 'unknown';
          setPlayerGrid(newGrid);
          commitHistory(newGrid);
          toast.error('Corrección: esa celda no debe ser negra');
          return;
        }
      }
    }
    // 2. Revelar una celda negra no marcada
    const candidates: [number, number][] = [];
    for (let r = 0; r < puzzle.solution.rows; r++)
      for (let c = 0; c < puzzle.solution.cols; c++) {
        if (puzzle.board.grid[r][c].clue !== null) continue;
        if (puzzle.solution.grid[r][c].state === CellState.BLACK && playerGrid[r][c] !== 'black')
          candidates.push([r, c]);
      }
    if (candidates.length > 0) {
      const [r, c] = candidates[Math.floor(Math.random() * candidates.length)];
      const newGrid = playerGrid.map(row => [...row]);
      newGrid[r][c] = 'black';
      setPlayerGrid(newGrid);
      commitHistory(newGrid);
      toast.success('Pista: esa celda es negra');
      checkVictory(newGrid, puzzle);
    } else {
      toast.info('¡El tablero está completo!');
    }
  }

  function showSolution() {
    if (!puzzle || !window.confirm('¿Ver la solución? No se guardará el progreso.')) return;
    const solGrid = puzzle.solution.grid.map(row =>
      row.map(cell => cell.state === CellState.BLACK ? 'black' as PlayerState : 'white' as PlayerState)
    );
    setPlayerGrid(solGrid);
    commitHistory(solGrid);
    toast.info('Solución revelada');
  }

  function checkVictory(grid: PlayerState[][], p: Puzzle) {
    if (solved) return;
    for (let r = 0; r < p.solution.rows; r++)
      for (let c = 0; c < p.solution.cols; c++) {
        if (p.board.grid[r][c].clue !== null) continue;
        const real = p.solution.grid[r][c].state;
        const ps = grid[r][c];
        if (real === CellState.BLACK && ps !== 'black') return;
        if (real === CellState.WHITE && ps === 'black') return;
      }
    setSolved(true);
    setVictoryModalOpen(true);
    toast.success('¡Tasukuea completado!');
    saveProgressData(p).catch(console.error);
  }

  const saveProgressData = useCallback(async (p: Puzzle) => {
    if (!user) return;
    try {
      await saveGameProgress({ tablero_id: p.metadata.seed, tiempo: elapsedTime, movimientos: moves, completado: true });
      await saveSessionResult({ tiempo: elapsedTime, movimientos: moves, completado: true, datos_juego: { tablero_id: p.metadata.seed } });
    } catch (e) { console.error(e); }
  }, [user, elapsedTime, moves, saveGameProgress, saveSessionResult]);

  function formatTime(s: number) { return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`; }

  // ─── Modo asistido ────────────────────────────────────────────────────────
  // Cada componente negro conexo debe ser un cuadrado perfecto (bbox cuadrado y totalmente lleno).
  // Si no lo es, todas sus celdas son errores.
  function calcularErrores(grid: PlayerState[][], rows: number, cols: number): Set<string> {
    const errors = new Set<string>();
    const visited = Array.from({ length: rows }, () => Array(cols).fill(false));

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (grid[r]?.[c] !== 'black' || visited[r][c]) continue;
        // BFS: recogemos el componente conexo
        const comp: [number, number][] = [];
        const stack: [number, number][] = [[r, c]];
        visited[r][c] = true;
        while (stack.length) {
          const [cr, cc] = stack.pop()!;
          comp.push([cr, cc]);
          for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) {
            const nr = cr + dr, nc = cc + dc;
            if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && !visited[nr][nc] && grid[nr]?.[nc] === 'black') {
              visited[nr][nc] = true;
              stack.push([nr, nc]);
            }
          }
        }
        // Calcular bbox
        const minR = Math.min(...comp.map(([rr]) => rr));
        const maxR = Math.max(...comp.map(([rr]) => rr));
        const minC = Math.min(...comp.map(([, cc]) => cc));
        const maxC = Math.max(...comp.map(([, cc]) => cc));
        const h = maxR - minR + 1;
        const w = maxC - minC + 1;
        // Cuadrado perfecto: bbox cuadrado + todas las celdas del bbox están en el componente
        if (h !== w || comp.length !== h * w) {
          for (const [rr, cc] of comp) errors.add(`${rr}-${cc}`);
        }
      }
    }
    return errors;
  }

  // ─── Loading ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="glass-container max-w-md w-full text-center">
          <div className="w-16 h-16 border-4 border-neureka-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Generando Tasukuea...</h2>
          <p className="text-white/70">Calculando puzzle único por lógica</p>
        </div>
      </div>
    );
  }

  if (!puzzle) return null;

  return (
    <div className="h-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Barra superior */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">⬛ Tasukuea</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {DIFFICULTIES.map(d => (
            <button key={d.id}
              onClick={() => navigate(`/juegos/tasukuea/${d.id}`)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${difficulty === d.id ? 'bg-white text-slate-800' : 'bg-white/20 text-white hover:bg-white/30'}`}>
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Deshacer */}
          <button onClick={undo} disabled={histIdx <= 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Rehacer */}
          <button onClick={redo} disabled={histIdx >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Pista */}
          <button onClick={giveHint} disabled={solved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solución */}
          <button onClick={showSolution} disabled={solved} title="Ver solución"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reiniciar */}
          <button onClick={reset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* Nuevo tablero */}
          <button onClick={() => { setSearchParams({}); generateNewPuzzle(true); }} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Cómo se juega */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-black/10 px-3 py-1 flex items-center gap-4 text-white/70 text-xs">
        <span>{currentDiff.icono} {currentDiff.nombre} — {puzzle.board.rows}×{puzzle.board.cols}</span>
        <span>⏱ {formatTime(elapsedTime)}</span>
        <span>🎯 {moves} mov</span>
      </div>

      {/* Tablero */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        {boardW > 0 && boardH > 0 && (() => {
          const rows = puzzle.board.rows;
          const cols = puzzle.board.cols;
          const errores = modoAsistido ? calcularErrores(playerGrid, rows, cols) : new Set<string>();
          const gap = 2;
          const pad = 4;
          const cellW = Math.floor((boardW - pad * 2 - gap * (cols - 1)) / cols);
          const cellH = Math.floor((boardH - pad * 2 - gap * (rows - 1)) / rows);
          const minCell = Math.min(cellW, cellH);
          const fontSize = Math.floor(minCell * 0.42);

          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
              gridTemplateRows: `repeat(${rows}, ${cellH}px)`,
              gap,
              backgroundColor: highContrast ? '#888888' : '#334155',
              padding: pad,
              borderRadius: 10,
            }}>
              {puzzle.board.grid.map((row, r) =>
                row.map((cell, c) => {
                  const ps = playerGrid[r]?.[c] ?? 'unknown';
                  const isClue = cell.clue !== null;
                  const hasError = errores.has(`${r}-${c}`);

                  let bg = highContrast ? '#888888' : '#e2e8f0';
                  if (isClue) bg = highContrast ? '#FFE600' : '#fde68a';
                  else if (ps === 'black') bg = hasError ? '#7f1d1d' : (highContrast ? '#000000' : '#1e293b');
                  else if (ps === 'white') bg = '#bbf7d0';

                  let textColor = highContrast ? '#000000' : '#92400e';
                  if (!isClue && ps === 'black') textColor = '#ffffff';
                  else if (!isClue && ps !== 'black') textColor = '#334155';

                  return (
                    <div
                      key={`${r}-${c}`}
                      onClick={() => handleCellClick(r, c)}
                      style={{
                        width: cellW, height: cellH,
                        backgroundColor: bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize, fontWeight: 'bold', color: textColor,
                        cursor: isClue ? 'default' : 'pointer',
                        borderRadius: 4,
                        userSelect: 'none',
                        border: isClue ? '3px solid #b45309' : ps === 'black' ? `2px solid ${hasError ? '#ef4444' : '#0f172a'}` : '2px solid #94a3b8',
                        outline: hasError ? '2px solid #ef4444' : undefined,
                        outlineOffset: hasError ? '-2px' : undefined,
                        transition: 'background-color 0.1s',
                        position: 'relative',
                      }}
                      onMouseEnter={e => { if (!isClue) (e.currentTarget as HTMLElement).style.filter = 'brightness(0.92)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.filter = ''; }}
                    >
                      {isClue && (
                        <span style={{ fontSize, fontWeight: 'bold', color: highContrast ? '#000000' : '#92400e' }}>
                          {cell.clue}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}
      </div>

      {/* Modales */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Tasukuea"
        gameIcon="⬛"
        objective="Coloca cuadrados negros en el tablero siguiendo las pistas numéricas. Cada número indica la suma de celdas negras de todas las regiones adyacentes."
        rules={[
          'Los cuadrados negros deben ser cuadrados perfectos: 1×1, 2×2, 3×3...',
          'Dos cuadrados negros no pueden tocarse ortogonalmente (sí en diagonal).',
          'Todas las celdas blancas deben formar una única región conectada.',
          'Cada celda blanca debe tener al menos un cuadrado negro adyacente.',
          'El número en una celda = suma de tamaños (área) de todos los cuadrados negros adyacentes.',
        ]}
        howToPlay="Haz clic en una celda para ciclar: vacío → negro → marcado (círculo azul) → vacío. Las celdas con número son fijas."
        author="Nikoli"
      />

      <VictoryModal
        isOpen={victoryModalOpen}
        onClose={() => setVictoryModalOpen(false)}
        stats={{ tiempo: elapsedTime, movimientos: moves }}
      />
    </div>
  );
}
